import React, { useEffect, useState } from 'react';
import { View } from '../../types/view';
import { useProWorkspace } from '../../hooks/useProWorkspace';
import { Lead, ProJob, Quote, JobStatus, ProInvoice } from '../../types/pro';
import { addDays, emptyQuoteLine, hoursOnJob, newLineId, nextJobNo, nextNumber, toISODate } from '../../utils/pro';
import { emptyBilling, nextInvoiceNo } from '../../utils/invoice';
import { CustomerFormModal } from '../../components/pro/CustomerFormModal';
import { NewJobModal } from '../../components/pro/NewJobModal';
import { primaryContact } from '../../utils/contacts';
import { ProDashboard } from './ProDashboard';
import { ProLeads } from './ProLeads';
import { ProCustomers } from './ProCustomers';
import { ProCustomerDetail } from './ProCustomerDetail';
import { ProQuotes } from './ProQuotes';
import { ProQuoteEditor } from './ProQuoteEditor';
import { ProJobs } from './ProJobs';
import { ProJobDetail } from './ProJobDetail';
import { ProCalendar } from './ProCalendar';
import { ProTime } from './ProTime';
import { ProMaterials } from './ProMaterials';
import { ProProducts } from './ProProducts';
import { ProInvoices } from './ProInvoices';
import { ProInvoiceEditor } from './ProInvoiceEditor';
import { ProEmployees } from './ProEmployees';
import { ProStatistics } from './ProStatistics';
import { ProSettings } from './ProSettings';
interface ProWorkspaceProps {
  view: View;
  onNavigate: (view: View) => void;
  data: ReturnType<typeof useProWorkspace>;
  /** Record the shell asked us to open, e.g. from global search. */
  selection: {
    kind: 'customer' | 'job' | 'quote' | 'lead';
    id: string;
  } | null;
  onSelectionHandled: () => void;
  isAdmin: boolean;
}

/**
 * Owns the Pro workspace: selection state and the hand-offs between lead,
 * quote, job and invoice. Data is loaded by the shell so search and the
 * quality section can read the same records.
 */
export function ProWorkspace({
  view,
  onNavigate,
  data,
  selection,
  onSelectionHandled,
  isAdmin
}: ProWorkspaceProps) {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [focusLeadId, setFocusLeadId] = useState<string | undefined>();
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  /** Customer id to pre-select, or '' for none. null means closed. */
  const [newJobFor, setNewJobFor] = useState<string | null>(null);

  // Global search hands us a record id; adopt it, then clear the request.
  useEffect(() => {
    if (!selection) return;
    if (selection.kind === 'customer') setCustomerId(selection.id);
    if (selection.kind === 'job') setJobId(selection.id);
    if (selection.kind === 'quote') setQuoteId(selection.id);
    if (selection.kind === 'lead') setFocusLeadId(selection.id);
    onSelectionHandled();
  }, [selection, onSelectionHandled]);
  const {
    customers,
    employees,
    leads,
    quotes,
    jobs,
    materials,
    invoices,
    timeEntries,
    products,
    settings,
    saveSettings
  } = data;

  // Shared tools live outside this workspace; don't intercept them.
  if (!view.startsWith('pro-')) return null;
  if (data.loading) {
    return <div className="flex min-h-[50vh] items-center justify-center" data-id="element-2238" data-mp="2bf5hltu9rkv5">
        <p className="text-sm text-stone-500" data-id="element-2239" data-mp="2z3y8vczy54jf">Henter data…</p>
      </div>;
  }
  const openJob = (id: string) => {
    setJobId(id);
    onNavigate('pro-job-detail');
  };
  const openQuote = (id: string) => {
    setQuoteId(id);
    onNavigate('pro-quote-editor');
  };
  const openCustomer = (id: string) => {
    setCustomerId(id);
    onNavigate('pro-customer-detail');
  };
  const openInvoice = (id: string) => {
    setInvoiceId(id);
    onNavigate('pro-invoice-editor');
  };
  const nextCustomerNo = () => nextNumber(customers.data.map(item => item.customerNo), 'K');

  // --- Invoices -----------------------------------------------------------
  const blankInvoice = (overrides: Partial<ProInvoice> = {}): Omit<ProInvoice, 'id'> => ({
    invoiceNo: nextInvoiceNo(invoices.data.map(item => item.invoiceNo)),
    customerId: '',
    customerName: '',
    billing: emptyBilling(),
    status: 'kladde',
    invoiceDate: toISODate(new Date()),
    dueDate: toISODate(addDays(new Date(), settings.paymentTermsDays)),
    paymentTerms: 'netto',
    paymentTermsDays: settings.paymentTermsDays,
    pricesIncludeVat: false,
    reference: '',
    orderNo: '',
    lines: [],
    totalDiscount: 0,
    notes: '',
    internalNote: '',
    payments: [],
    ...overrides
  });
  const newInvoice = async () => {
    const id = await invoices.add(blankInvoice());
    openInvoice(id);
  };
  const copyInvoice = async (source: ProInvoice) => {
    const id = await invoices.add(blankInvoice({
      customerId: source.customerId,
      customerName: source.customerName,
      billing: {
        ...source.billing
      },
      jobId: source.jobId,
      reference: source.reference,
      orderNo: source.orderNo,
      pricesIncludeVat: source.pricesIncludeVat,
      totalDiscount: source.totalDiscount,
      notes: source.notes,
      // A fresh document needs fresh line ids.
      lines: source.lines.map(line => ({
        ...line,
        id: newLineId()
      }))
    }));
    openInvoice(id);
  };
  const creditNote = async (source: ProInvoice) => {
    const id = await invoices.add(blankInvoice({
      customerId: source.customerId,
      customerName: source.customerName,
      billing: {
        ...source.billing
      },
      jobId: source.jobId,
      reference: source.reference,
      pricesIncludeVat: source.pricesIncludeVat,
      totalDiscount: source.totalDiscount,
      creditNoteOf: source.id,
      notes: `Kreditnota for faktura ${source.invoiceNo}.`,
      // Negative quantities reverse the original, so the two net to zero.
      lines: source.lines.map(line => ({
        ...line,
        id: newLineId(),
        quantity: -Math.abs(line.quantity)
      }))
    }));
    openInvoice(id);
  };

  // --- Chain: lead -> customer + job -------------------------------------
  const convertLead = async (lead: Lead, alsoCreateJob: boolean) => {
    const newCustomerId = await customers.add({
      customerNo: nextCustomerNo(),
      type: 'privat',
      name: lead.name,
      address: lead.address,
      postalCode: '',
      city: lead.city,
      phone: lead.phone,
      email: lead.email,
      notes: lead.notes,
      createdDate: toISODate(new Date()),
      leadSource: lead.source
    });
    if (alsoCreateJob) {
      await jobs.add({
        jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
        customerId: newCustomerId,
        customerName: lead.name,
        address: lead.address,
        city: lead.city,
        title: lead.jobType,
        description: lead.description,
        status: 'ikke-planlagt',
        durationHours: 4,
        employeeIds: lead.ownerId ? [lead.ownerId] : [],
        estimatedHours: 4,
        materials: [],
        photos: [],
        documents: [],
        notes: ''
      });
    }
    if (lead.id) {
      await leads.update(lead.id, {
        stage: 'vundet',
        customerId: newCustomerId,
        lastActivityDate: toISODate(new Date()),
        activity: [...lead.activity, {
          id: `a-${Date.now()}`,
          date: toISODate(new Date()),
          text: alsoCreateJob ? 'Vundet — oprettet som kunde og opgave.' : 'Vundet — oprettet som kunde.',
          author: 'Dig'
        }]
      });
    }
  };

  // --- Chain: lead -> quote ----------------------------------------------
  const quoteFromLead = async (lead: Lead) => {
    const existing = customers.data.find(c => c.name === lead.name);
    const id = await quotes.add({
      quoteNo: nextNumber(quotes.data.map(q => q.quoteNo), 'T'),
      customerId: existing?.id ?? '',
      customerName: lead.name,
      title: lead.jobType,
      status: 'kladde',
      createdDate: toISODate(new Date()),
      validUntil: toISODate(addDays(new Date(), settings.quoteValidDays)),
      lines: [emptyQuoteLine()],
      notes: lead.description,
      leadId: lead.id
    });
    openQuote(id);
  };

  // --- Chain: quote -> job ------------------------------------------------
  const jobFromQuote = async (quote: Quote) => {
    const customer = customers.data.find(c => c.id === quote.customerId);
    const id = await jobs.add({
      jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
      customerId: quote.customerId,
      customerName: quote.customerName,
      address: customer?.address ?? '',
      city: customer?.city ?? '',
      title: quote.title,
      description: quote.notes,
      status: 'ikke-planlagt',
      durationHours: 4,
      employeeIds: [],
      estimatedHours: quote.lines.filter(line => line.unit.startsWith('time')).reduce((sum, line) => sum + line.quantity, 0),
      materials: quote.lines.filter(line => line.materialId).map(line => {
        const material = materials.data.find(m => m.id === line.materialId);
        return {
          id: newLineId(),
          materialId: line.materialId,
          name: line.description,
          itemNo: material?.itemNo ?? '—',
          quantity: line.quantity,
          costPrice: material?.costPrice ?? 0,
          salesPrice: line.unitPrice
        };
      }),
      photos: [],
      documents: [],
      notes: '',
      quoteId: quote.id
    });
    if (quote.id) await quotes.update(quote.id, {
      jobId: id
    });
    openJob(id);
  };

  // --- Chain: job -> invoice ----------------------------------------------
  const invoiceFromJob = async (job: ProJob) => {
    const hours = hoursOnJob(timeEntries.data, job.id ?? '');
    const rate = typeof job.hourlyRate === 'number' ? job.hourlyRate : settings.hourlyRate;
    const lines: ProInvoice['lines'] = [];
    if (job.billingType === 'fast-pris') {
      // A fixed price is billed as one agreed sum; the hours and materials
      // behind it are our own cost, not the customer's line items.
      lines.push({
        id: newLineId(),
        description: job.title || 'Aftalt arbejde',
        quantity: 1,
        unit: 'stk',
        unitPrice: job.fixedPrice ?? 0,
        discount: 0,
        discountType: 'procent',
        vatRate: settings.vatRate
      });
    } else {
      if (hours > 0) {
        lines.push({
          id: newLineId(),
          description: 'Arbejdsløn',
          quantity: hours,
          unit: 'timer',
          unitPrice: rate,
          discount: 0,
          discountType: 'procent',
          vatRate: settings.vatRate
        });
      }
      job.materials.forEach(material => lines.push({
        id: newLineId(),
        description: material.name,
        quantity: material.quantity,
        unit: 'stk',
        unitPrice: material.salesPrice,
        discount: 0,
        discountType: 'procent',
        vatRate: settings.vatRate
      }));
    }
    const customer = customers.data.find(item => item.id === job.customerId);
    const id = await invoices.add(blankInvoice({
      customerId: job.customerId,
      customerName: job.customerName,
      billing: {
        name: job.customerName,
        attention: customer ? primaryContact(customer)?.name ?? '' : '',
        address: customer?.address ?? job.address,
        postalCode: customer?.postalCode ?? '',
        city: customer?.city ?? job.city,
        email: customer?.email ?? '',
        phone: customer?.phone ?? '',
        cvr: customer?.cvr ?? ''
      },
      jobId: job.id,
      reference: job.jobNo,
      lines
    }));
    if (job.id) await jobs.update(job.id, {
      status: 'faktureret',
      invoiceId: id
    });
    openInvoice(id);
  };
  const createJobAt = async (start: Date, employeeId: string) => {
    const id = await jobs.add({
      jobNo: nextJobNo(jobs.data.map(j => j.jobNo)),
      customerId: '',
      customerName: 'Ny aftale',
      address: '',
      city: '',
      title: 'Ny aftale',
      description: '',
      status: 'planlagt',
      start: start.toISOString(),
      durationHours: 2,
      employeeIds: employeeId ? [employeeId] : [],
      estimatedHours: 2,
      materials: [],
      photos: [],
      documents: [],
      notes: ''
    });
    openJob(id);
  };
  const job = jobs.data.find(item => item.id === jobId);
  const quote = quotes.data.find(item => item.id === quoteId);
  const customer = customers.data.find(item => item.id === customerId);
  const invoice = invoices.data.find(item => item.id === invoiceId);

  /** Available from anywhere a customer can be created. */
  const customerModal = <CustomerFormModal isOpen={newCustomerOpen} onClose={() => setNewCustomerOpen(false)} nextCustomerNo={nextCustomerNo()} onSave={async draft => {
    const id = await customers.add(draft);
    // On the invoice editor, adopt the customer we just created.
    if (view === 'pro-invoice-editor' && invoice?.id) {
      await invoices.update(invoice.id, {
        customerId: id,
        customerName: draft.name,
        billing: {
          name: draft.name,
          attention: draft.contacts?.find(contact => contact.primary)?.name ?? '',
          address: draft.address,
          postalCode: draft.postalCode,
          city: draft.city,
          email: draft.email,
          phone: draft.phone,
          cvr: draft.cvr ?? ''
        }
      });
    }
  }} data-id="element-2240" data-mp="3esscwlx1w66j" />;

  /**
   * Creating a job straight from the Opgaver page or a customer — most work
   * for a small firm never goes through a quote.
   */
  const newJobModal = <NewJobModal isOpen={newJobFor !== null} onClose={() => setNewJobFor(null)} customers={customers.data} employees={employees.data} company={settings} nextJobNo={nextJobNo(jobs.data.map(j => j.jobNo))} presetCustomerId={newJobFor || undefined} onCreateCustomer={() => setNewCustomerOpen(true)} onSave={async draft => {
    const id = await jobs.add(draft);
    openJob(id);
  }} data-id="element-2241" data-mp="2lt6ogl7a0r1l" />;
  switch (view) {
    case 'pro-dashboard':
      return <ProDashboard quotes={quotes.data} jobs={jobs.data} invoices={invoices.data} employees={employees.data} timeEntries={timeEntries.data} company={settings} onOpenJob={openJob} onOpenInvoice={openInvoice} onOpenQuote={openQuote} onNavigate={target => onNavigate(target === 'jobs' ? 'pro-jobs' : target === 'invoices' ? 'pro-invoices' : target === 'time' ? 'pro-time' : 'pro-calendar')} onStartJob={item => item.id && jobs.update(item.id, {
        status: 'i-gang'
      })} data-id="element-2242" data-mp="3efzthfrp8che" />;
    case 'pro-leads':
      return <ProLeads leads={leads.data} employees={employees.data} onUpdate={(id, changes) => leads.update(id, changes)} onCreate={lead => leads.add(lead)} onConvert={convertLead} onMakeQuote={quoteFromLead} focusLeadId={focusLeadId} onClearFocus={() => setFocusLeadId(undefined)} data-id="element-2243" data-mp="p5cjykp5xkbj" />;
    case 'pro-customers':
      return <>
          <ProCustomers customers={customers.data} jobs={jobs.data} onOpen={openCustomer} onNew={() => setNewCustomerOpen(true)} data-id="element-2244" data-mp="17w3zy53no8yw" />
          {customerModal}
        </>;
    case 'pro-customer-detail':
      return customer ? <>
          <ProCustomerDetail customer={customer} jobs={jobs.data} quotes={quotes.data} invoices={invoices.data} timeEntries={timeEntries.data} employees={employees.data} onBack={() => onNavigate('pro-customers')} onOpenJob={openJob} onOpenQuote={openQuote} onOpenInvoice={openInvoice} onUpdate={changes => customer.id && customers.update(customer.id, changes)} onNewJob={() => setNewJobFor(customer.id ?? '')} data-id="element-2245" data-mp="32azpongtji6j" />
          {newJobModal}
        </> : null;
    case 'pro-quotes':
      return <ProQuotes quotes={quotes.data} vatRate={settings.vatRate} onOpen={openQuote} onNew={async () => {
        const id = await quotes.add({
          quoteNo: nextNumber(quotes.data.map(q => q.quoteNo), 'T'),
          customerId: '',
          customerName: '',
          title: '',
          status: 'kladde',
          createdDate: toISODate(new Date()),
          validUntil: toISODate(addDays(new Date(), settings.quoteValidDays)),
          lines: [emptyQuoteLine()],
          notes: ''
        });
        openQuote(id);
      }} data-id="element-2246" data-mp="2xgxyxiez7yy" />;
    case 'pro-quote-editor':
      return quote ? <ProQuoteEditor quote={quote} customers={customers.data} materials={materials.data} company={settings} onBack={() => onNavigate('pro-quotes')} onChange={changes => quote.id && quotes.update(quote.id, changes)} onCreateJob={jobFromQuote} data-id="element-2247" data-mp="2l2gpdhp7g6uy" /> : null;
    case 'pro-jobs':
      return <>
          <ProJobs jobs={jobs.data} employees={employees.data} timeEntries={timeEntries.data} onOpen={openJob} onSetStatus={(item, status: JobStatus) => item.id && jobs.update(item.id, {
          status
        })} onNew={() => setNewJobFor('')} data-id="element-2248" data-mp="3w1uprkytzxif" />
          {newJobModal}
          {customerModal}
        </>;
    case 'pro-job-detail':
      return job ? <ProJobDetail job={job} employees={employees.data} materials={materials.data} timeEntries={timeEntries.data} companyHourlyRate={settings.hourlyRate} onBack={() => onNavigate('pro-jobs')} onUpdate={changes => job.id && jobs.update(job.id, changes)} onAddTime={entry => timeEntries.add(entry)} onUpdateTime={(id, changes) => timeEntries.update(id, changes)} onRemoveTime={id => timeEntries.remove(id)} onCreateInvoice={invoiceFromJob} data-id="element-2249" data-mp="3d4js24sulc53" /> : null;
    case 'pro-calendar':
      return <ProCalendar jobs={jobs.data} employees={employees.data} onOpenJob={openJob} onUpdateJob={(id, changes) => jobs.update(id, changes)} onCreateAt={createJobAt} data-id="element-2250" data-mp="2msgemco9b5ql" />;
    case 'pro-time':
      return <ProTime jobs={jobs.data} employees={employees.data} timeEntries={timeEntries.data} onAdd={entry => timeEntries.add(entry)} onUpdate={(id, changes) => timeEntries.update(id, changes)} onRemove={id => timeEntries.remove(id)} data-id="element-2251" data-mp="2d0iyqsmazplj" />;
    case 'pro-materials':
      return <ProMaterials materials={materials.data} onAdd={material => materials.add(material)} onUpdate={(id, changes) => materials.update(id, changes)} onRemove={id => materials.remove(id)} data-id="element-2252" data-mp="1u4ke0wvkgtoj" />;
    case 'pro-products':
      return <ProProducts products={products.data} onAdd={product => products.add(product)} onUpdate={(id, changes) => products.update(id, changes)} onRemove={id => products.remove(id)} data-id="element-2253" data-mp="k6qmw4s3imyh" />;
    case 'pro-invoices':
      return <ProInvoices invoices={invoices.data} company={settings} onOpen={openInvoice} onNew={newInvoice} onCopy={copyInvoice} onCreditNote={creditNote} data-id="element-2254" data-mp="5hueu7q1h9xv" />;
    case 'pro-invoice-editor':
      return invoice ? <>
          <ProInvoiceEditor invoice={invoice} customers={customers.data} jobs={jobs.data} products={products.data} employees={employees.data} timeEntries={timeEntries.data} company={settings} onBack={() => onNavigate('pro-invoices')} onChange={changes => invoice.id && invoices.update(invoice.id, changes)} onCreateCustomer={() => setNewCustomerOpen(true)} data-id="element-2255" data-mp="3nmxo84n1g843" />
          {customerModal}
        </> : null;
    case 'pro-employees':
      return <ProEmployees employees={employees.data} jobs={jobs.data} timeEntries={timeEntries.data} isAdmin={isAdmin} onOpenJob={openJob} onAdd={employee => employees.add(employee)} onUpdate={(id, changes) => employees.update(id, changes)} onRemove={id => employees.remove(id)} data-id="element-2256" data-mp="1y5k4p4wid00r" />;
    case 'pro-statistics':
      return <ProStatistics leads={leads.data} quotes={quotes.data} jobs={jobs.data} invoices={invoices.data} employees={employees.data} timeEntries={timeEntries.data} vatRate={settings.vatRate} data-id="element-2257" data-mp="1fesw45kchdnf" />;
    case 'pro-settings':
      return <ProSettings company={settings} employees={employees.data} onSave={saveSettings} onReimportLegacy={data.reimportLegacy} data-id="element-2258" data-mp="2vrzinwpq7fjk" />;
    default:
      return null;
  }
}